import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { OptionTypeBase, SelectComponentsConfig, Props as SelectProps } from 'react-select';
import type { AllColorType, AllIconType, SizeType } from '../../util/types';
import type { FieldGroupProps } from '../formGroup/types';
import {
  AsyncSelectorStyled,
  CreatableSelectorStyled,
  SelectorIconStyled,
  SelectorStyled,
  SelectorWrappedStyled,
} from './Selector.styled';
import { CustomClearIndicator, CustomDropdownIndicator, CustomNoOptionsMessage } from './components';
import { getIconComponent } from '../../util/reactFunc';
import { PREFIX, SELECTOR_ICON, SELECTOR_WRAPPER } from '../../util/data-id';
import FieldGroup from '../formGroup/FieldGroup';

type ComponentsType = keyof SelectComponentsConfig<OptionTypeBase, boolean>;

type Ref = HTMLElement;
interface Props extends Omit<SelectProps, 'id'>, FieldGroupProps {
  /**
   * Renders component as async.
   * https://react-select.com/async
   */
  isAsync?: boolean;
  /**
   * Renders component as creatable.
   * https://react-select.com/creatable
   */
  isCreatable?: boolean;
  /**
   * Sets selector size.
   */
  size?: SizeType;
  /**
   * Sets selector color.
   */
  color?: AllColorType;
  /**
   * Sets an icon to the left of the selector.
   */
  icon?: AllIconType;
  /**
   * Adds or overrides components.
   */
  customComponents?: Record<ComponentsType, JSX.Element>;
  /**
   * Disables the selector. Alias for react-select's `isDisabled`, matching the native `disabled`
   * naming convention used by this library's other input components.
   */
  disabled?: boolean;
}

const Selector = React.forwardRef<Ref, Props>(({
  id,
  label,
  hint,
  hideLabel = false,
  labelStyle = null,
  isAsync = false,
  isCreatable = false,
  size = 'md',
  color = 'primary',
  icon,
  customComponents,
  error = null,
  required = false,
  isClearable = true,
  isLoading = false,
  disabled = false,
  ...other
}: Props, ref): JSX.Element => {

  const { translate } = useI18n();

  const clear_indicator = (props: SelectProps) => {
    return <CustomClearIndicator size={size} {...props} />;
  };

  const dropdown_indicator = (props: SelectProps) => {
    return <CustomDropdownIndicator size={size} {...props} />;
  };

  const no_options_message = (props: SelectProps) => {
    return <CustomNoOptionsMessage message={translate('components.SELECTORS.NO_OPTIONS.DEFAULT')} {...props} />;
  };

  const default_components = {
    DropdownIndicator: dropdown_indicator,
    ClearIndicator: clear_indicator,
    NoOptionsMessage: no_options_message,
  };

  const rendered_props = {
    innerRef: ref,
    classNamePrefix: PREFIX,
    inputId: id,
    hideSelectedOptions: false,
    isClearable,
    isDisabled: disabled,
    $hasIcon: !!icon,
    $size: size,
    $color: color,
    placeholder:
      isLoading
        ? translate('global.LOADING')
        : 'placeholder' in other
          ? other.placeholder
          : other.isMulti ?
            translate(
              'components.SELECTORS.PLACEHOLDER.MULTI') : translate('components.SELECTORS.PLACEHOLDERS.SINGLE',
            ),
    components: Object.assign(default_components, customComponents),
    ...(other as SelectProps),
  };

  function selector() {
    if (isAsync) {
      return (<AsyncSelectorStyled {...rendered_props} />);
    }

    if (isCreatable) {
      return (<CreatableSelectorStyled {...rendered_props} />);
    }

    return (<SelectorStyled {...rendered_props} />);
  }

  return (
    <FieldGroup
      id={id}
      label={label}
      hint={hint}
      hideLabel={hideLabel}
      labelStyle={labelStyle}
      error={error}
      required={required}
    >
      <SelectorWrappedStyled data-id={SELECTOR_WRAPPER} $color={color}>
        {icon && (
          <SelectorIconStyled data-id={SELECTOR_ICON} $size={size}>
            {getIconComponent(icon, size)}
          </SelectorIconStyled>
        )}
        {selector()}
      </SelectorWrappedStyled>
    </FieldGroup>
  );
});

export default Selector;
